package com.tixforgigs.storefront

import com.tixforgigs.apax.models.Flavor
import com.tixforgigs.apax.models.GrantType
import com.tixforgigs.common.core.enums.ClientType
import com.tixforgigs.common.core.enums.View
import com.tixforgigs.common.core.extensions.RoutingUtil.APP_VIEW_ID
import com.tixforgigs.common.core.extensions.RoutingUtil.AppScope
import com.tixforgigs.common.core.extensions.RoutingUtil.appRoot
import com.tixforgigs.common.core.extensions.RoutingUtil.mount
import com.tixforgigs.common.core.extensions.RoutingUtil.mountIfAuthorized
import com.tixforgigs.common.core.extensions.RoutingUtil.routing
import com.tixforgigs.common.core.extensions.navigateIfAuthorizedTo
import com.tixforgigs.common.core.extensions.navigateTo
import com.tixforgigs.common.core.extensions.orElse
import com.tixforgigs.common.core.extensions.tryCatchSentry
import com.tixforgigs.common.core.theme.TixTheme
import com.tixforgigs.common.core.util.*
import com.tixforgigs.common.core.util.AuthUtil.authAuthorizeRequest
import com.tixforgigs.common.core.util.AuthUtil.authExpiredChecker
import com.tixforgigs.common.core.util.AuthUtil.authTokenRequests
import com.tixforgigs.common.core.util.AuthUtil.redirectWithPath
import com.tixforgigs.common.core.util.StorageUtil.deleteLocalStorageItem
import com.tixforgigs.common.core.util.StorageUtil.getLocalStorageItem
import com.tixforgigs.common.core.util.UiUtil.appBase
import com.tixforgigs.common.http.HttpClient
import com.tixforgigs.common.http.HttpClient.FLAVOR
import com.tixforgigs.common.ui.pages.error.ErrorView
import com.tixforgigs.common.ui.pages.login.ForgotPasswordView
import com.tixforgigs.common.ui.pages.login.LoginView
import com.tixforgigs.common.ui.pages.login.ResetPasswordView
import com.tixforgigs.common.ui.pages.template.TemplateConfiguratorDetailView
import com.tixforgigs.common.ui.pages.template.TemplateConfiguratorOverviewView
import io.github.oshai.kotlinlogging.KotlinLoggingConfiguration
import io.github.oshai.kotlinlogging.Level
import io.kvision.Application
import io.kvision.panel.Root
import io.kvision.routing.Routing
import io.kvision.routing.Strategy
import io.kvision.theme.ThemeManager
import kotlinx.browser.window
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.launch
import kotlin.uuid.ExperimentalUuidApi
import kotlin.uuid.Uuid

class TemplateConfiguratorApp : Application(), CoroutineScope by CoroutineScope(Dispatchers.Default) {

  init {
    HttpClient.ensureInitialized()
    KotlinLoggingConfiguration.logLevel = when (FLAVOR) {
      Flavor.PROD -> Level.OFF
      Flavor.STAGING -> Level.WARN
      Flavor.DEV -> Level.INFO
      Flavor.LOCAL -> Level.TRACE
      else -> Level.OFF
    }

    //LifecycleUtil.init()
    ThemeManager.init(initialTheme = TixTheme.initialTheme)
    EmbedUtil.init()
    LocalizationUtil.init()
    SentryUtil.init()
    ToastUtil.init()

    launch {
      authExpiredChecker()
    }
  }

  @OptIn(ExperimentalUuidApi::class)
  override fun start() {
    appRoot = Root(APP_VIEW_ID)
    routing = Routing.init(root = appBase, useHash = false, strategy = Strategy.ONE).also { routing ->

      routing
        .on(View.ABSOLUTE_ROOT.path, {
          routing.navigateIfAuthorizedTo(View.TEMPLATE_CONFIGURATIONS)
        })
        .on(View.TEMPLATE_CONFIGURATIONS.path, {
          mountIfAuthorized(TemplateConfiguratorOverviewView())
        })
        .on(View.TEMPLATE_CONFIGURATION(":templateId").path, { path ->
          tryCatchSentry { Uuid.parse(path.data.templateId) }.onSuccess { templateId ->
            mountIfAuthorized(TemplateConfiguratorDetailView(templateId = templateId))
          }
        })
        .on(View.LOGIN.path, {
          mount(LoginView())
        })
        .on(View.FORGOT_PASSWORD.path, {
          mount(ForgotPasswordView())
        })
        .on(View.RESET_PASSWORD.path, { path ->
          tryCatchSentry { path.params.token as String }.onSuccess { token ->
            mount(ResetPasswordView(token))
          }
        })
        .on(View.AUTHORIZE.path, {
          authAuthorizeRequest(
            scopes = listOf("Tmpl.Config"), //todo needed as enum from apax
            onCompletionWithUrl = { url ->
              window.location.href = url
            }
          )
        })
        .on(View.TOKEN.path, { path ->
          tryCatchSentry { path.params.code as String }.onSuccess { code ->
            AppScope.launch {
              val grantType = getLocalStorageItem<GrantType>(StorageUtil.StorageKey.AuthGrantType)
                .orElse { GrantType.PASSWORD }
                .also { deleteLocalStorageItem(StorageUtil.StorageKey.AuthGrantType) }

              authTokenRequests(
                code = code,
                grantType = grantType,
                clientType = ClientType.TEMPLATE_CONFIGURATOR,
                onCompletionWithPath = { path ->
                  redirectWithPath(path)
                },
                onCompletionFallback = {
                  routing.navigateTo(View.TEMPLATE_CONFIGURATIONS)
                }
              )
            }
          }
        })
        .notFound({
          mount(ErrorView())
        })

      routing.resolve()
    }
  }

}